import React, { useEffect, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaDownload,
  FaFileImport,
  FaInfoCircle,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { SiMicrosoftexcel } from "react-icons/si";
import { toast } from "react-toastify";

import { uploadJudokasFile } from "../../../services/competiteursServices";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const MAX_FILE_SIZE_MB = 10;

const formatFileSize = (bytes = 0) => {
  if (!bytes) return "0 Ko";

  const ko = bytes / 1024;
  if (ko < 1024) return `${ko.toFixed(1)} Ko`;

  return `${(ko / 1024).toFixed(2)} Mo`;
};

const getFileExtension = (fileName = "") => {
  const clean = String(fileName || "").toLowerCase();
  const index = clean.lastIndexOf(".");
  return index >= 0 ? clean.slice(index) : "";
};

const validateFile = (file) => {
  if (!file) {
    return "Veuillez sélectionner un fichier.";
  }

  const extension = getFileExtension(file.name);

  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return "Format invalide. Formats acceptés : .xlsx, .xls ou .csv.";
  }

  const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;

  if (file.size > maxBytes) {
    return `Le fichier est trop lourd. Taille maximale : ${MAX_FILE_SIZE_MB} Mo.`;
  }

  return "";
};

const requiredColumns = [
  {
    name: "LICENCE",
    description: "Numéro de licence, facultatif",
  },
  {
    name: "NOM",
    description: "Nom du judoka",
  },
  {
    name: "PRENOM",
    description: "Prénom du judoka",
  },
  {
    name: "SEXE",
    description: "M ou F",
  },
  {
    name: "CLUB",
    description: "Nom du club",
  },
  {
    name: "NAISSANCE",
    description: "Format JJ/MM/AAAA",
  },
  {
    name: "GRADE",
    description: "Grade optionnel",
  },
];

const UploadJudokasModal = ({
  isOpen,
  onClose,
  competitionId,
  refreshJudokas,
}) => {
  const inputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFile(null);
    setDragActive(false);
    setIsUploading(false);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [isOpen]);

  const selectFile = (file) => {
    const message = validateFile(file);

    if (message) {
      toast.error(message);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    selectFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(false);

    const file = event.dataTransfer.files?.[0];
    selectFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(false);
  };

  const clearFile = () => {
    setSelectedFile(null);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();

    const message = validateFile(selectedFile);

    if (message) {
      toast.error(message);
      return;
    }

    try {
      setIsUploading(true);

      await uploadJudokasFile(competitionId, selectedFile);

      toast.success("Judokas importés avec succès !");
      refreshJudokas?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur import judokas :", error);
      toast.error("Erreur lors de l'importation des judokas.");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="
        fixed inset-0 z-[99999] flex h-[100dvh] w-full items-end justify-center
        overflow-hidden bg-slate-950/70 px-2 backdrop-blur-sm
        sm:items-center sm:px-4
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Importer des judokas"
        className="
          flex max-h-[96dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[2rem]
          border border-white/20 bg-white text-slate-950 shadow-2xl
          dark:border-white/10 dark:bg-slate-950 dark:text-white
          sm:max-h-[92dvh] sm:rounded-[2rem]
        "
      >
        <header className="relative shrink-0 overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 px-4 py-4 text-white sm:px-6 sm:py-5">
          <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-emerald-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-orange-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.26em] text-emerald-300">
                Import compétiteurs
              </p>

              <h2 className="mt-1 flex items-center gap-2 text-xl font-bold leading-tight sm:text-2xl">
                <SiMicrosoftexcel className="h-6 w-6 text-emerald-300" />
                Importer des judokas
              </h2>

              <p className="mt-1 max-w-2xl text-sm text-slate-300">
                Importez un fichier Excel ou CSV pour ajouter ou mettre à jour
                rapidement les compétiteurs de la compétition.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="
                inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl
                border border-white/10 bg-white/10 text-white transition
                hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300
                disabled:opacity-60
              "
              aria-label="Fermer"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </header>

        <form onSubmit={handleUploadSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="min-w-0 space-y-5">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`
                    relative rounded-3xl border-2 border-dashed p-5 transition
                    ${
                      dragActive
                        ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10"
                        : selectedFile
                        ? "border-emerald-300 bg-emerald-50/80 dark:border-emerald-400/30 dark:bg-emerald-500/10"
                        : "border-slate-300 bg-slate-50/80 hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-emerald-500/10"
                    }
                  `}
                >
                  <input
                    ref={inputRef}
                    id="upload-judokas-file"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <div className="flex flex-col items-center text-center">
                    <div
                      className={`
                        mb-4 flex h-16 w-16 items-center justify-center rounded-3xl shadow-lg
                        ${
                          selectedFile
                            ? "bg-emerald-600 text-white shadow-emerald-500/20"
                            : "bg-slate-950 text-white shadow-slate-950/20 dark:bg-white dark:text-slate-950"
                        }
                      `}
                    >
                      {selectedFile ? (
                        <FaCheckCircle className="h-7 w-7" />
                      ) : (
                        <FaCloudUploadAlt className="h-8 w-8" />
                      )}
                    </div>

                    <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                      {selectedFile
                        ? "Fichier prêt à importer"
                        : "Déposez votre fichier ici"}
                    </h3>

                    <p className="mt-1 max-w-xl text-sm text-slate-500 dark:text-slate-400">
                      Formats acceptés : Excel `.xlsx`, `.xls` ou CSV. Taille
                      maximale : {MAX_FILE_SIZE_MB} Mo.
                    </p>

                    {selectedFile ? (
                      <div className="mt-4 w-full max-w-xl rounded-2xl border border-emerald-200 bg-white p-3 text-left shadow-sm dark:border-emerald-400/20 dark:bg-slate-900">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                            <SiMicrosoftexcel className="h-6 w-6" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                              {selectedFile.name}
                            </p>

                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {formatFileSize(selectedFile.size)} —{" "}
                              {getFileExtension(selectedFile.name).toUpperCase()}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={clearFile}
                            disabled={isUploading}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600 transition hover:bg-rose-100 disabled:opacity-60 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
                            title="Retirer le fichier"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label
                        htmlFor="upload-judokas-file"
                        className="
                          mt-5 inline-flex min-h-11 cursor-pointer items-center justify-center gap-2
                          rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white
                          shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700
                        "
                      >
                        <FaUpload className="h-4 w-4" />
                        Sélectionner un fichier
                      </label>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                      <FaInfoCircle className="h-4 w-4" />
                    </div>

                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                        Règles d’import
                      </h3>

                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Les judokas déjà présents seront mis à jour s’ils sont
                        reconnus par le couple{" "}
                        <span className="font-bold text-slate-700 dark:text-slate-200">
                          Nom + Prénom
                        </span>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <InfoPill text="Les poids seront saisis ensuite via la pesée." />
                    <InfoPill text="La licence est facultative." />
                    <InfoPill text="La date de naissance doit être au format JJ/MM/AAAA." />
                    <InfoPill text="Le grade est optionnel." />
                  </div>
                </div>
              </div>

              <aside className="min-w-0">
                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.03] lg:sticky lg:top-4">
                  <div className="mb-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-300">
                      Format attendu
                    </p>

                    <h3 className="mt-1 text-lg font-bold text-slate-950 dark:text-white">
                      Colonnes du fichier
                    </h3>
                  </div>

                  <div className="grid gap-2">
                    {requiredColumns.map((column) => (
                      <div
                        key={column.name}
                        className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="rounded-lg bg-slate-950 px-2 py-1 text-xs font-bold text-white dark:bg-white dark:text-slate-950">
                            {column.name}
                          </span>
                        </div>

                        <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                          {column.description}
                        </p>
                      </div>
                    ))}
                  </div>

                  <a
                    href="/modele_import_judokas.xlsx"
                    download
                    className="
                      mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2
                      rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2
                      text-sm font-bold text-emerald-700 transition hover:bg-emerald-100
                      dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20
                    "
                  >
                    <FaDownload className="h-4 w-4" />
                    Télécharger le modèle
                  </a>
                </div>
              </aside>
            </div>
          </div>

          <footer className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Un seul fichier peut être importé à la fois.
              </p>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isUploading}
                  className="
                    inline-flex min-h-11 items-center justify-center gap-2 rounded-xl
                    border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700
                    transition hover:bg-slate-50 disabled:opacity-60
                    dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-white/10
                  "
                >
                  <FaTimes className="h-4 w-4" />
                  Fermer
                </button>

                <button
                  type="submit"
                  disabled={isUploading || !selectedFile}
                  className="
                    inline-flex min-h-11 items-center justify-center gap-2 rounded-xl
                    bg-emerald-600 px-5 py-2 text-sm font-bold text-white
                    shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700
                    disabled:cursor-not-allowed disabled:opacity-60
                  "
                >
                  <FaFileImport className="h-4 w-4" />
                  {isUploading ? "Importation..." : "Importer le fichier"}
                </button>
              </div>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
};

const InfoPill = ({ text }) => {
  return (
    <div className="flex items-start gap-2 rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
      <FaCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
      <p className="text-xs font-semibold leading-relaxed text-slate-600 dark:text-slate-300">
        {text}
      </p>
    </div>
  );
};

export default UploadJudokasModal;